/* eslint-disable react/prop-types */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, Checkbox, Flex, HStack, Input, InputGroup, InputLeftElement, Portal, Table, Tbody,
  Td, Text, Th, Thead, Tr, useBreakpointValue, useColorModeValue, useToast, Wrap, WrapItem,
} from '@chakra-ui/react';
import {
  FiSearch, FiChevronUp, FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import ReactDOM from 'react-dom';
import { useConfirm } from './ConfirmProvider';
import SectionCaughtUp from './SectionCaughtUp';

// item 1268 — ONE shared viewport-bottom host for every ledger's floating bulk bar. Two
// ledgers with live selections (Chad: Enter-to-QB + Deposits at once) STACK their bars
// instead of overlapping at the same fixed spot (which read as "my options disappeared").
// item 1268b (Chad: "gray out other sections — check something in one, you can't check the
// others") — EXCLUSIVE selection across every ledger on screen: the first ledger with a
// selection CLAIMS the bulk flow; every other ledger's checkboxes disable until it's cleared.
// One selection, one bar, zero ambiguity about which red buttons you're about to press.
interface LedgerClaim { id: string; title?: string }
let ledgerClaim: LedgerClaim | null = null;
const ledgerClaimSubs = new Set<() => void>();
const setLedgerClaim = (c: LedgerClaim | null): void => {
  ledgerClaim = c;
  ledgerClaimSubs.forEach((f) => f());
};

let floatingBarHost: HTMLDivElement | null = null;
const getFloatingBarHost = (): HTMLDivElement => {
  if (!floatingBarHost || !document.body.contains(floatingBarHost)) {
    floatingBarHost = document.createElement('div');
    Object.assign(floatingBarHost.style, {
      position: 'fixed',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '1400',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      pointerEvents: 'none',
      maxWidth: '96vw',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(floatingBarHost);
  }
  return floatingBarHost;
};

// item 481 — the reusable accountant LEDGER: one combined list (active + history) per area with
// search, sortable columns, checkbox multi-select + a bulk-action bar (by group), per-row actions
// (by 1), and load-more. Enter-to-QuickBooks / Payroll+ / Deposit & Collections all render through
// this so the accountant dashboard reads as one consistent grammar (Chad).
export interface LedgerColumn<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right';
  // item 496 — a FIXED width (e.g. '110px'), so this column lands at the same x-position in
  // every table using this column set (default table-layout:auto sizes each instance by its own
  // content, which is why sibling ledgers — e.g. two staff members' commission tables — didn't
  // line up). Leave unset on the wide identity/description column(s) so they share the remainder.
  width?: string;
  // item 1444 (Chad: "the name being cut off is bananas") — a FLOOR for a flexible (width-less)
  // column. Default floor is 160px, which is what squeezed the invoice NAME to "Sunset Station…"
  // next to ~1200px of fixed siblings. Only feeds the table's min-width; the column still
  // shares the remainder above the floor.
  minWidth?: string;
  // item 1088 (mobile polish) — how this column behaves in the phone CARD layout:
  //   'primary' — the card's title zone (defaults to the FIRST column)
  //   'inline'  — a small chip beside the title (defaults for header-less icon columns)
  //   'hide'    — not worth a phone pixel (e.g. LAST MODIFIED BY)
  // Unset columns render as label/value pairs in the card's detail grid.
  mobile?: 'primary' | 'inline' | 'hide';
}

export interface LedgerBulkAction<T> {
  label: string;
  icon?: React.ReactElement;
  colorScheme?: string;
  // item 1047 (Chad) — optional role label ("Accountant" / "Non-accountant"): consecutive
  // actions sharing a section render grouped under a small uppercase chip so it's crystal
  // clear WHO each button is for.
  section?: string;
  // limit the action to eligible rows (e.g. only "New" rows can be marked entered)
  appliesTo?: (row: T) => boolean;
  // guided confirm text; return '' to skip the confirm
  confirm?: (rows: T[]) => string;
  onRun: (rows: T[]) => Promise<void> | void;
}

interface LedgerTableProps<T> {
  rows: T[];
  columns: LedgerColumn<T>[];
  rowId: (row: T) => string;
  bulkActions?: LedgerBulkAction<T>[];
  // item 1268 (Chad: "not clear which one to use" with two ledgers on one page) — the FLOATING
  // copy of the bulk bar identifies its ledger by name and lists what's checked above the
  // buttons. Title like "Enter to QuickBooks — Invoices" / "Deposits"; label = one line per row.
  bulkBarTitle?: string;
  bulkRowLabel?: (row: T) => string;
  // item 1277 — extra content on the search/count row (e.g. Deposits' "Open AR $X" chip)
  headerExtra?: React.ReactNode;
  rowActions?: (row: T) => React.ReactNode;
  // item 1447 (Chad: "the name should ride underneath each column or something. I'm tired of the
  // name cutting off or going to a second line") — an optional FULL-WIDTH line under every row
  // (spans all data columns; the sticky actions column stays dark beside it). The job name lives
  // here, never in a squeezed cell, so it can't truncate or wrap. Renders inside the phone card
  // under the title too.
  rowSubline?: (row: T) => React.ReactNode;
  searchText?: (row: T) => string;
  searchPlaceholder?: string;
  // item 732 (Chad) — optional filter controls (e.g. the ALL STATUSES select) rendered INLINE on
  // the search row (to the left of the search box) so filters + search share one row, not two.
  filters?: React.ReactNode;
  emptyText?: string;
  // shown under the green check when the whole ledger is empty (defaults to emptyText)
  caughtUpText?: string;
  pageSize?: number;
  // optional: clicking a row's data cells fires this (checkbox + row-actions cells don't).
  onRowClick?: (row: T) => void;
  // item 485 — optional grouping: rows are kept contiguous per group (group is the primary
  // sort) and a full-width subheader row renders whenever the group key changes.
  groupBy?: (row: T) => string;
  groupHeader?: (key: string, rows: T[]) => React.ReactNode;
  // item 573 — every group subheader is a real disclosure button: click folds/unfolds JUST
  // that group (default expanded; the summary line stays visible when folded). Pass this
  // localStorage key to persist the folded groups per-device (like OrderableSections /
  // item 478); omit for session-only fold state.
  groupCollapseKey?: string;
  // item 1051 (Chad: "100s of entries — do you infinitely scroll?") — the history partition:
  // rows where isDone is true sink BELOW the actionable set. Actionable rows always ALL
  // render (work must never hide behind pagination); done/history rows cap at doneCap with
  // a Show-more. An active search bypasses the cap so old history stays findable.
  isDone?: (row: T) => boolean;
  doneCap?: number;
  // item 1331 (Chad: "the ledgers should default to last proposal/invoice/WO/SO/PO/bill/sub on
  // top… right now it is chaos") — the initial column sort before the user clicks a header.
  // Document ledgers pass { key: 'number', dir: -1 } (newest first); accounting ledgers omit it.
  // `value` sorts by something that has no column of its own (e.g. a sub's start date).
  defaultSort?: { key: string; dir: 1 | -1; value?: (row: T) => string | number };
  // item 492 — a read-only running list: hide the select checkboxes entirely (no bulk actions)
  selectable?: boolean;
  // item 547 — a fixed width for the row-actions column. The default (a single ⋮) is tiny;
  // a multi-button cluster (primary action + quick actions + overflow) needs a real width or
  // it overflows LEFT over the last data column (table-layout:fixed). e.g. '250px'.
  rowActionsWidth?: string;
  // item 1449 — opt-in: the actions column takes NO fixed width, so with table-layout:fixed it absorbs
  // the page's leftover width and every DATA column keeps its exact px (the document ledgers on a
  // project page line up column-for-column). Default false = the item-1349c behaviour (fixed width).
  rowActionsFlex?: boolean;
  // item 547 — optional header for the row-actions column (e.g. 'ACTION'); default blank.
  rowActionsHeader?: string;
  // item 579 — optional cap on how many rows may be selected at once (e.g. the Deposits
  // ledger passes 1 while method = Check: one check ⇒ one invoice). Once the cap is hit,
  // every UNCHECKED box disables (visually locked) until a row is unchecked or the caller
  // lifts the cap; select-all disables whenever it would blow past the cap.
  maxSelectable?: number;
  // item 590 — optional per-row selectability. Non-selectable rows show a disabled checkbox and
  // are excluded from select-all (e.g. the Deposits ledger only lets you select Pending-accountant
  // rows — an already-Deposited row is terminal, not re-markable). Undefined = every row selectable.
  isRowSelectable?: (row: T) => boolean;
  // item 1167 — gray a terminal-but-kept row (reversed/cancelled): visible, untouchable
  isRowDimmed?: (row: T) => boolean;
  // item 1167 — compact row padding (long ledgers: keep actions reachable without a trek)
  dense?: boolean;
  // item 1245 (Chad) — an optional SECTION tier ABOVE the groups: rows mapping to a non-null
  // section render under a bold divider band, and their group subheaders indent + dim beneath
  // it (Deposits: DEPOSITED HISTORY › customer › rows vs the active customer groups).
  sectionBy?: (row: T) => string | null;
  // item 1357 (Chad: "organize them into folders once they are entered") — groups that start
  // FOLDED. The persisted fold set then records TOGGLES FROM THE DEFAULT (folded = in-set XOR
  // default), so a history folder opens/closes and remembers it per-device like any other group.
  defaultCollapsed?: (groupKey: string) => boolean;
  // item 1358 — which sections MUTE their groups (indent + dim). Default: every section (the
  // 1245 behavior). Deposits now has a WORK tier ("To reconcile") that must read at full weight,
  // so it mutes only the history band.
  sectionMuted?: (section: string) => boolean;
  // item 1191 — per-user column resize: every header edge is a drag handle; widths persist
  // per-device in localStorage. The storage key defaults to the ledger's column-key signature
  // (two tables sharing a column set — e.g. the per-tab Enter-to-QB instances — deliberately
  // share widths). Pass an explicit key only to separate tables that would otherwise collide.
  resizeKey?: string;
}

function LedgerTable<T>(props: LedgerTableProps<T>): React.ReactElement {
  const confirm = useConfirm();
  const {
    rows, columns, rowId, bulkActions, bulkBarTitle, bulkRowLabel, headerExtra, rowActions, rowSubline, searchText, filters, onRowClick, groupBy, groupHeader,
    searchPlaceholder = 'Search…', emptyText = 'Nothing here.', caughtUpText, pageSize = 25,
    selectable = true, rowActionsWidth, rowActionsFlex = false, rowActionsHeader, groupCollapseKey, maxSelectable,
    isRowSelectable, isDone, doneCap = 30, isRowDimmed, dense, resizeKey, sectionBy, defaultSort, defaultCollapsed, sectionMuted,
  } = props;
  const canSelect = (row: T): boolean => (isRowSelectable ? isRowSelectable(row) : true);
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(defaultSort?.key ?? null); // item 1331
  const [sortDir, setSortDir] = useState<1 | -1>(defaultSort?.dir ?? 1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // item 573 — per-group fold state, kept as the COLLAPSED set (so an unseen/new group
  // defaults to expanded). Keyed by group key; each bucket folds independently. Optionally
  // seeded from + persisted to localStorage (see groupCollapseKey above).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    if (!groupCollapseKey) return new Set();
    try {
      const raw = JSON.parse(window.localStorage.getItem(groupCollapseKey) ?? '[]');
      return new Set<string>(Array.isArray(raw) ? raw.filter((k) => typeof k === 'string') : []);
    } catch { return new Set(); }
  });
  const [limit, setLimit] = useState(pageSize);
  const [running, setRunning] = useState('');
  const toast = useToast();
  const headBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const barBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  // item 547 — a clearly-visible row hover (the old whiteAlpha.100 barely showed on the
  // Aurora gradient — "no hover", Chad).
  const hoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  // item 1088 — below md the ledger renders as CARDS: a squeezed table is desktop UI shrunk,
  // not mobile UI. Every consumer (CRM, Deposits, Enter-to-QB, …) gets this for free.
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  // fixed width contributed by the row-actions column to the table's min width
  let actionsColW = 0;
  if (rowActions) actionsColW = rowActionsWidth ? parseInt(rowActionsWidth, 10) : 90;

  // item 1444 — a column's contribution to the table's min-width: its declared width, else its
  // declared floor, else the 160px default floor (item 498).
  const floorWidth = (c: LedgerColumn<T>): number => {
    if (c.width) return parseInt(c.width, 10);
    if (c.minWidth) return parseInt(c.minWidth, 10);
    return 160;
  };
  // item 1191 — per-user column widths. Stored per-device under the column signature (or the
  // caller's resizeKey); a stored width overrides the column's declared width AND the 160px
  // flexible floor. Drag a header edge to size; double-click the handle to reset that column.
  const widthStoreKey = `basix:ledgerw:${resizeKey ?? columns.map((c) => c.key).join(',')}`;
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = JSON.parse(window.localStorage.getItem(widthStoreKey) ?? '{}');
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const clean: Record<string, number> = {};
        Object.entries(raw).forEach(([k, v]) => { if (typeof v === 'number' && v >= 60) clean[k] = v; });
        return clean;
      }
    } catch { /* no storage */ }
    return {};
  });
  const persistWidths = (next: Record<string, number>) => {
    try { window.localStorage.setItem(widthStoreKey, JSON.stringify(next)); } catch { /* no storage */ }
  };
  // live drag bookkeeping (refs, not state — only the width value re-renders)
  const dragRef = useRef<{ key: string; startX: number; startW: number } | null>(null);
  const startResize = (key: string, e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).closest('th');
    dragRef.current = { key, startX: e.clientX, startW: th ? th.offsetWidth : 160 };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const w = Math.max(60, Math.round(d.startW + (ev.clientX - d.startX)));
      setColWidths((prev) => ({ ...prev, [d.key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const d = dragRef.current;
      dragRef.current = null;
      if (d) {
        setColWidths((prev) => { persistWidths(prev); return prev; });
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const resetColumn = (key: string) => setColWidths((prev) => {
    const next = { ...prev };
    delete next[key];
    persistWidths(next);
    return next;
  });
  const colWidth = (c: LedgerColumn<T>): string | undefined => (
    colWidths[c.key] !== undefined ? `${colWidths[c.key]}px` : c.width
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let r = (s && searchText) ? rows.filter((x) => searchText(x).toLowerCase().includes(s)) : rows;
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      // item 1331 — a default sort may key off a value with no column (falls back to it)
      const sortValue = col?.sortValue ?? (sortKey === defaultSort?.key ? defaultSort?.value : undefined);
      if (sortValue) {
        r = [...r].sort((a, b) => {
          const av = sortValue(a); const bv = sortValue(b);
          if (av < bv) return -1 * sortDir;
          if (av > bv) return 1 * sortDir;
          return 0;
        });
      }
    }
    // grouping keeps group members contiguous: group key is the PRIMARY sort, the
    // column sort (or original order) applies within each group (stable sort).
    if (groupBy) r = [...r].sort((a, b) => groupBy(a).localeCompare(groupBy(b)));
    // item 1051 — the history partition is the OUTERMOST sort: actionable first, done last
    // (stable — the sorts above hold within each half)
    if (isDone) r = [...r.filter((x) => !isDone(x)), ...r.filter(isDone)];
    return r;
  }, [rows, q, sortKey, sortDir, columns, searchText, groupBy, isDone, defaultSort]);

  // item 1051 — partitioned paging: EVERY actionable row renders (never behind a cap);
  // done/history rows cap at doneCap (Show-more grows it); searching shows all matches.
  const [doneLimit, setDoneLimit] = useState(doneCap);
  const searching = q.trim() !== '';
  let shown: T[];
  let hiddenCount: number;
  if (isDone) {
    const active = filtered.filter((r) => !isDone(r));
    const done = filtered.filter(isDone);
    const doneShown = searching ? done : done.slice(0, doneLimit);
    shown = [...active, ...doneShown];
    hiddenCount = done.length - doneShown.length;
  } else {
    shown = filtered.slice(0, limit);
    hiddenCount = filtered.length - shown.length;
  }
  const selectedRows = filtered.filter((r) => selected.has(rowId(r)));
  // select-all covers the WHOLE filtered set, not just the visible page — otherwise a bulk
  // action (e.g. "mark everything entered") silently skips rows below the page cap (which is
  // how CRM/Users rows, sorted after invoices, were being left un-entered).
  // item 590 — select-all covers only the SELECTABLE filtered rows (non-selectable rows, e.g.
  // already-deposited, are never part of the set to check)
  const selectableFiltered = filtered.filter(canSelect);
  const allShownSelected = selectableFiltered.length > 0
    && selectableFiltered.every((r) => selected.has(rowId(r)));
  // item 579 — the selection cap is hit: every unchecked box locks until one is unchecked
  const selectionFull = maxSelectable !== undefined && selectedRows.length >= maxSelectable;

  // item 1267 — is the in-place bulk bar on screen? While rows are selected and it isn't,
  // a floating copy pins to the viewport bottom so the actions never need a scroll-back.
  const barRef = useRef<HTMLDivElement | null>(null);
  const [barInView, setBarInView] = useState(true);
  const hasSelection = selectedRows.length > 0;

  // item 1268b — the exclusive-selection claim: claim while we have a selection, release on
  // clear/unmount; re-render when any ledger's claim changes so our checkboxes gray out.
  const instanceId = useRef(`lt_${Math.random().toString(36).slice(2)}`).current;
  const [, forceClaimRender] = useState(0);
  useEffect(() => {
    const f = (): void => forceClaimRender((n) => n + 1);
    ledgerClaimSubs.add(f);
    return () => { ledgerClaimSubs.delete(f); };
  }, []);
  useEffect(() => {
    if (hasSelection && (!ledgerClaim || ledgerClaim.id !== instanceId)) setLedgerClaim({ id: instanceId, title: bulkBarTitle });
    else if (!hasSelection && ledgerClaim?.id === instanceId) setLedgerClaim(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelection]);
  useEffect(() => () => { if (ledgerClaim?.id === instanceId) setLedgerClaim(null); }, [instanceId]);
  const otherClaim = ledgerClaim && ledgerClaim.id !== instanceId ? ledgerClaim : null;
  const claimHint = otherClaim
    ? `A selection is active in ${otherClaim.title ?? 'another ledger'} — act on it or Clear it first.`
    : undefined;
  useEffect(() => {
    if (!hasSelection) { setBarInView(true); return undefined; }
    const el = barRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(([e]) => setBarInView(e.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasSelection]);
  const floatBg = useColorModeValue('white', 'gray.800');
  // item 1449 (Chad: "you have this weird locked section on the right side") — the pinned, dark
  // actions column (item 1349c) only earns its keep when the table actually scrolls sideways.
  // Watch the scroll container: sticky + solid background + divider ONLY while it overflows;
  // otherwise the actions cell is a plain cell like every other.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile, rows, columns, colWidths]);
  const pinProps = overflowing
    ? {
      position: 'sticky' as const, right: 0, zIndex: 1, bg: floatBg, borderLeftWidth: '1px', borderLeftColor: cardBorder,
    }
    : {};
  const floatBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  // select-all would blow past the cap — lock it (unless it's the deselect-all direction)
  const selectAllLocked = maxSelectable !== undefined && !allShownSelected
    && selectableFiltered.length > maxSelectable;

  const toggle = (id: string) => {
    if (otherClaim) return; // item 1268b — another ledger owns the selection flow
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (otherClaim) return; // item 1268b
    setSelected((prev) => {
      const n = new Set(prev);
      if (allShownSelected) selectableFiltered.forEach((r) => n.delete(rowId(r)));
      else selectableFiltered.forEach((r) => n.add(rowId(r)));
      return n;
    });
  };
  const setSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(1); }
  };
  // item 573 — folding a group only HIDES its rows; it never touches the selection set.
  // That deliberately matches the select-all rule above: rows a bulk action should cover
  // must never be silently dropped just because they're off-screen (below the page cap OR
  // inside a folded bucket). The "N selected" pill in the bulk bar keeps the count honest.
  const toggleGroup = (gk: string) => setCollapsedGroups((prev) => {
    const n = new Set(prev);
    if (n.has(gk)) n.delete(gk); else n.add(gk);
    if (groupCollapseKey) {
      try { window.localStorage.setItem(groupCollapseKey, JSON.stringify(Array.from(n))); } catch { /* no storage */ }
    }
    return n;
  });

  // item 1177b (Chad) — expand/collapse ALL groups at once (grouped ledgers only). "All
  // collapsed" is judged against the groups currently on screen; the persisted set may
  // carry stale keys, which is harmless.
  const allGroupKeys = groupBy
    ? Array.from(new Set(filtered.map((r) => groupBy(r) ?? ''))) : [];
  const isFolded = (k: string) => collapsedGroups.has(k) !== !!defaultCollapsed?.(k); // item 1357 — XOR with the default
  const allGroupsFolded = allGroupKeys.length > 0 && allGroupKeys.every((k) => isFolded(k));
  const toggleAllGroups = () => setCollapsedGroups(() => {
    // the set holds toggles-from-default: to fold everything mark the groups that default OPEN; to unfold, the ones that default FOLDED
    const n = new Set(allGroupKeys.filter((k) => (allGroupsFolded ? !!defaultCollapsed?.(k) : !defaultCollapsed?.(k))));
    if (groupCollapseKey) {
      try { window.localStorage.setItem(groupCollapseKey, JSON.stringify(Array.from(n))); } catch { /* no storage */ }
    }
    return n;
  });

  const runBulk = async (action: LedgerBulkAction<T>) => {
    const eligible = action.appliesTo ? selectedRows.filter(action.appliesTo) : selectedRows;
    if (eligible.length === 0) {
      toast({ status: 'warning', title: `No selected rows apply to “${action.label}”.` });
      return;
    }
    const msg = action.confirm ? action.confirm(eligible) : '';
    if (msg && !(await confirm(msg, { confirmLabel: action.label }))) return;
    setRunning(action.label);
    try {
      await action.onRun(eligible);
      setSelected(new Set());
    } catch (e) {
      // item 574 — an onRun that already toasted its own explanation throws an error flagged
      // `handled: true`; keep the selection and skip the generic failure toast for those.
      if (!(e as { handled?: boolean })?.handled) {
        toast({ status: 'error', title: `“${action.label}” failed`, description: 'Please try again.' });
      }
    }
    setRunning('');
  };

  // whole ledger empty → the celebratory green check (not just a filtered-out search)
  if (rows.length === 0) {
    return <SectionCaughtUp compact text={caughtUpText ?? emptyText} />;
  }

  return (
    <Box>
      <Flex mb={3} align="center" wrap="wrap" sx={{ gap: '12px' }}>
        {/* item 732 (Chad) — filters (e.g. ALL STATUSES) sit inline, left of the search */}
        {filters}
        {searchText && (
          <InputGroup size="sm" maxW="320px">
            <InputLeftElement pointerEvents="none"><FiSearch /></InputLeftElement>
            <Input placeholder={searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)} borderRadius="lg" variant="filled" />
          </InputGroup>
        )}
        <Text fontSize="xs" color={subColor}>{`${filtered.length} item${filtered.length === 1 ? '' : 's'}`}</Text>
        {/* item 1177b — one-click fold/unfold for every group in a grouped ledger */}
        {groupBy && allGroupKeys.length > 1 && (
          <Button size="xs" variant="ghost" onClick={toggleAllGroups}>
            {allGroupsFolded ? 'Expand all' : 'Collapse all'}
          </Button>
        )}
        {/* item 1277 — per-ledger extra (e.g. Deposits' Open AR total) rides this row */}
        {headerExtra}
      </Flex>

      {/* bulk-action bar — appears when rows are selected.
          item 1267 (Chad: "deep in a ledger you have to scroll back up to the red status
          options") — the SAME bar also FLOATS at the viewport bottom (via Portal — ancestors
          use overflow:hidden, so sticky can't work) whenever this in-place copy is scrolled
          out of view. Same buttons, same handlers; always in reach. */}
      {bulkActions && bulkActions.length > 0 && selectedRows.length > 0 && (() => {
        const barContent = (
          <>
            <Text fontSize="sm" fontWeight="medium">{`${selectedRows.length} selected`}</Text>
            {/* item 1047 — actions grouped under small role chips when a section is named */}
            {(() => {
              const groups: { name: string | null; actions: LedgerBulkAction<T>[] }[] = [];
              bulkActions.forEach((a) => {
                const name = a.section ?? null;
                const last = groups[groups.length - 1];
                if (last && last.name === name) last.actions.push(a);
                else groups.push({ name, actions: [a] });
              });
              return groups.map((g) => (
                <Flex
                  key={g.name ?? 'default'}
                  align="center"
                  sx={{ gap: '6px' }}
                  borderWidth={g.name ? '1px' : undefined}
                  borderRadius="md"
                  px={g.name ? 2 : 0}
                  py={g.name ? 1 : 0}
                >
                  {g.name && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" opacity={0.6} textTransform="uppercase">
                      {g.name}
                    </Text>
                  )}
                  {g.actions.map((a) => (
                    <Button
                      key={a.label}
                      size="xs"
                      colorScheme={a.colorScheme}
                      leftIcon={a.icon}
                      isLoading={running === a.label}
                      onClick={() => runBulk(a)}
                    >
                      {a.label}
                    </Button>
                  ))}
                </Flex>
              ));
            })()}
            <Button size="xs" variant="ghost" ml="auto" onClick={() => setSelected(new Set())}>Clear</Button>
          </>
        );
        return (
          <>
            <Flex ref={barRef} mb={2} p={2} align="center" wrap="wrap" bg={barBg} borderRadius="md" sx={{ gap: '8px' }}>
              {barContent}
            </Flex>
            {!barInView && ReactDOM.createPortal(
              <Box
                maxW="min(96vw, 1100px)"
                p={2}
                px={3}
                bg={floatBg}
                borderWidth="1px"
                borderColor={floatBorder}
                borderRadius="lg"
                boxShadow="dark-lg"
                pointerEvents="auto"
              >
                {/* item 1268 (Chad) — say WHICH ledger this bar belongs to… */}
                {bulkBarTitle && (
                  <Text fontSize="0.65rem" fontWeight="bold" letterSpacing="0.1em" textTransform="uppercase" opacity={0.7} mb={1}>
                    {bulkBarTitle}
                  </Text>
                )}
                {/* …and WHAT is checked, right above the buttons */}
                {bulkRowLabel && (
                  <Box maxH="76px" overflowY="auto" mb={1.5}>
                    {selectedRows.slice(0, 8).map((r) => (
                      <Text key={rowId(r)} fontSize="xs" opacity={0.75} noOfLines={1}>
                        {bulkRowLabel(r)}
                      </Text>
                    ))}
                    {selectedRows.length > 8 && (
                      <Text fontSize="xs" opacity={0.55}>{`+ ${selectedRows.length - 8} more`}</Text>
                    )}
                  </Box>
                )}
                <Flex align="center" wrap="wrap" sx={{ gap: '8px' }}>
                  {barContent}
                </Flex>
              </Box>,
              getFloatingBarHost(),
            )}
          </>
        );
      })()}

      {isMobile && (
        <Box>
          {shown.length === 0 && (
            <Text textAlign="center" color={subColor} py={6} fontSize="sm">No matches — try a different search.</Text>
          )}
          {shown.map((row, idx) => {
            const id = rowId(row);
            const key = groupBy?.(row);
            const prevKey = idx > 0 ? groupBy?.(shown[idx - 1]) : undefined;
            const showGroupHeader = groupBy && key !== prevKey;
            const groupFolded = groupBy !== undefined && isFolded(key ?? '');
            // item 1088 — card zones: primary (title), inline chips (header-less icon
            // columns), and the label/value detail grid; 'hide' columns don't spend pixels
            const visibleCols = columns.filter((c) => c.mobile !== 'hide');
            const primary = visibleCols.find((c) => c.mobile === 'primary') ?? visibleCols[0];
            const inline = visibleCols.filter((c) => c !== primary && (c.mobile === 'inline' || c.header === ''));
            const details = visibleCols.filter((c) => c !== primary && !inline.includes(c));
            return (
              <React.Fragment key={id}>
                {showGroupHeader && (
                  <Flex
                    as="button"
                    type="button"
                    aria-expanded={!groupFolded}
                    onClick={() => toggleGroup(key ?? '')}
                    align="center"
                    w="100%"
                    px={1}
                    py={2}
                    sx={{ gap: '8px' }}
                    textAlign="left"
                    fontWeight="semibold"
                    fontSize="sm"
                    id={`ledger-group-${(key ?? '').replace(/\s+/g, '-')}`}
                  >
                    <Box as={groupFolded ? FiChevronRight : FiChevronDown} flexShrink={0} />
                    <Box flex="1">
                      {groupHeader
                        ? groupHeader(key ?? '', filtered.filter((r) => groupBy!(r) === key))
                        : key}
                    </Box>
                  </Flex>
                )}
                {!groupFolded && (
                  <Box
                    borderWidth="1px"
                    borderColor={cardBorder}
                    borderRadius="lg"
                    p={3}
                    mb={2}
                    bg={selected.has(id) ? barBg : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    cursor={onRowClick ? 'pointer' : undefined}
                  >
                    <Flex align="flex-start" sx={{ gap: '10px' }}>
                      {selectable && (
                        <Box pt={0.5} onClick={(e) => e.stopPropagation()}>
                          <Checkbox size="lg" title={claimHint} isChecked={selected.has(id)} isDisabled={!canSelect(row) || (selectionFull && !selected.has(id)) || !!otherClaim} onChange={() => toggle(id)} />
                        </Box>
                      )}
                      <Box flex="1" minW={0}>{primary?.render(row)}</Box>
                      {inline.map((c) => (
                        <Box key={c.key} flexShrink={0} onClick={(e) => e.stopPropagation()}>{c.render(row)}</Box>
                      ))}
                    </Flex>
                    {/* item 1447 — the full-width name line, under the title zone on the card */}
                    {rowSubline && (
                      <Box mt={1} pl={selectable ? '38px' : 0} fontSize="sm">{rowSubline(row)}</Box>
                    )}
                    {details.length > 0 && (
                      <Wrap spacing={3} mt={2} pl={selectable ? '38px' : 0}>
                        {details.map((c) => {
                          const val = c.render(row);
                          if (val === null || val === undefined || val === '') return null;
                          return (
                            <WrapItem key={c.key} maxW="100%">
                              <Box>
                                <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" color={subColor}>
                                  {c.header}
                                </Text>
                                <Box fontSize="sm">{val}</Box>
                              </Box>
                            </WrapItem>
                          );
                        })}
                      </Wrap>
                    )}
                    {rowActions && (
                      <Flex mt={2} justify="flex-end" onClick={(e) => e.stopPropagation()} sx={{ gap: '6px' }} wrap="wrap">
                        {rowActions(row)}
                      </Flex>
                    )}
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {!isMobile && (
      <Box overflowX="auto" ref={scrollRef}>
        {/* item 498 — table-layout:fixed needs a MINIMUM total width, or a narrow (mobile)
            viewport squeezes the unset flexible columns toward zero — that's what turned
            "National Technology Associates" into one letter per line (Chad's screenshot). Give
            every fixed column its own width and every flexible column a 160px floor, then let
            this Box's overflowX:auto scroll horizontally below that width instead of crushing text. */}
        <Table
          size="sm"
          variant="simple"
          minW={`${columns.reduce((sum, c) => sum + (colWidths[c.key] ?? floorWidth(c)), 0)
            + (selectable ? 40 : 0) + actionsColW}px`}
          sx={{ tableLayout: 'fixed' }}
        >
          <Thead bg={headBg}>
            <Tr>
              {selectable && (
                <Th px={2} w="40px">
                  <Checkbox title={claimHint} isChecked={allShownSelected} isIndeterminate={selectedRows.length > 0 && !allShownSelected} isDisabled={selectAllLocked || !!otherClaim} onChange={toggleAll} />
                </Th>
              )}
              {columns.map((c) => (
                <Th
                  key={c.key}
                  width={colWidth(c)}
                  textAlign={c.align === 'right' ? 'right' : 'left'}
                  cursor={c.sortValue ? 'pointer' : undefined}
                  onClick={c.sortValue ? () => setSort(c.key) : undefined}
                  whiteSpace="nowrap"
                  position="relative"
                >
                  <HStack spacing={1} justify={c.align === 'right' ? 'flex-end' : 'flex-start'}>
                    <Text>{c.header}</Text>
                    {sortKey === c.key && (sortDir === 1 ? <FiChevronUp /> : <FiChevronDown />)}
                  </HStack>
                  {/* item 1191 — drag the header's right edge to resize; double-click resets.
                      A real element (not ::after) so it can stopPropagation past the sort click. */}
                  <Box
                    position="absolute"
                    top={0}
                    right="-4px"
                    bottom={0}
                    w="8px"
                    cursor="col-resize"
                    zIndex={1}
                    onMouseDown={(e) => startResize(c.key, e)}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => { e.stopPropagation(); resetColumn(c.key); }}
                    _hover={{ bg: hoverBg }}
                    borderRightWidth="2px"
                    borderRightColor={colWidths[c.key] !== undefined ? cardBorder : 'transparent'}
                  />
                </Th>
              ))}
              {/* item 1349c (Chad: "the three dots require scrolling to") — the actions column
                  is PINNED to the right edge of the scroll container */}
              {rowActions && (
                <Th
                  // item 1449 — rowActionsFlex: NO fixed width, so this one width-less column absorbs the
                  // page's leftover width and every DATA column keeps its exact px (rowActionsWidth still
                  // feeds the table's minimum width). Otherwise the item-1349c fixed width as before.
                  w={rowActionsFlex ? undefined : (rowActionsWidth ?? '1px')}
                  textAlign="right"
                  whiteSpace="nowrap"
                  {...pinProps}
                >
                  {rowActionsHeader}
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {shown.length === 0 && (
              <Tr><Td colSpan={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} textAlign="center" color={subColor} py={6}>No matches — try a different search.</Td></Tr>
            )}
            {shown.map((row, idx) => {
              const id = rowId(row);
              // item 485 — emit a full-width group subheader whenever the group key changes
              const key = groupBy?.(row);
              const prevKey = idx > 0 ? groupBy?.(shown[idx - 1]) : undefined;
              const showGroupHeader = groupBy && key !== prevKey;
              const groupFolded = groupBy !== undefined && isFolded(key ?? '');
              const span = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);
              // item 1245 — the SECTION tier: a bold divider band when the section changes
              const section = sectionBy?.(row) ?? null;
              const prevSection = idx > 0 ? (sectionBy?.(shown[idx - 1]) ?? null) : null;
              const showSectionHeader = sectionBy !== undefined && section !== null && section !== prevSection;
              return (
                <React.Fragment key={id}>
                  {showSectionHeader && (
                    <Tr>
                      <Td colSpan={span} p={0}>
                        <Flex align="center" px={3} py={2} mt={2} bg={barBg} borderTopWidth="2px" borderTopColor={cardBorder} sx={{ gap: '8px' }}>
                          <Text fontSize="xs" fontWeight="bold" letterSpacing="0.12em" textTransform="uppercase" color={subColor}>
                            {section}
                          </Text>
                        </Flex>
                      </Td>
                    </Tr>
                  )}
                  {showGroupHeader && (
                    // item 553 — a stable id per group so a section quick-nav can jump to it
                    <Tr bg={headBg} id={`ledger-group-${(key ?? '').replace(/\s+/g, '-')}`} sx={{ scrollMarginTop: '96px' }}>
                      {/* item 573 — the whole subheader row is a disclosure button: click
                          anywhere on it to fold/unfold JUST this bucket (same grammar as
                          OrderableSections' group headers — chevron leads, right = folded).
                          The summary line (the groupHeader content, e.g. "Chad Allen — 8
                          docs · $13,065.00") stays visible folded so the ledger stays
                          scannable. */}
                      <Td colSpan={span} p={0}>
                        <Flex
                          as="button"
                          type="button"
                          aria-expanded={!groupFolded}
                          onClick={() => toggleGroup(key ?? '')}
                          align="center"
                          w="100%"
                          px={4}
                          // item 1245 — inside a section the group indents + dims one step
                          pl={section !== null && (sectionMuted ? sectionMuted(section) : true) ? 10 : 4}
                          opacity={section !== null && (sectionMuted ? sectionMuted(section) : true) ? 0.85 : 1}
                          py={1.5}
                          sx={{ gap: '8px' }}
                          textAlign="left"
                          fontWeight="semibold"
                          fontSize="sm"
                        >
                          <Box as={groupFolded ? FiChevronRight : FiChevronDown} flexShrink={0} />
                          <Box flex="1">
                            {groupHeader
                              ? groupHeader(key ?? '', filtered.filter((r) => groupBy!(r) === key))
                              : key}
                          </Box>
                        </Flex>
                      </Td>
                    </Tr>
                  )}
                  {/* item 573 — a folded group's rows are simply not rendered (the cheap
                      conditional; no per-row state lives in the rows themselves) */}
                  {!groupFolded && (
                    <Tr
                      bg={selected.has(id) ? barBg : undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      cursor={onRowClick ? 'pointer' : undefined}
                      _hover={onRowClick ? { bg: hoverBg } : undefined}
                      // item 1447 — hovering the main row also tints its subline row
                      sx={onRowClick && rowSubline ? { '&:hover + tr': { bg: hoverBg } } : undefined}
                      transition="background 0.12s ease"
                      opacity={isRowDimmed?.(row) ? 0.45 : undefined}
                    >
                      {selectable && <Td px={2} py={dense ? 1 : undefined} pt={rowSubline ? 3 : undefined} pb={rowSubline ? 1 : undefined} borderBottom={rowSubline ? 'none' : undefined} onClick={(e) => e.stopPropagation()}><Checkbox title={claimHint} isChecked={selected.has(id)} isDisabled={!canSelect(row) || (selectionFull && !selected.has(id)) || !!otherClaim} onChange={() => toggle(id)} /></Td>}
                      {columns.map((c) => (
                        <Td key={c.key} py={dense ? 1 : undefined} pt={rowSubline ? 3 : undefined} pb={rowSubline ? 1 : undefined} borderBottom={rowSubline ? 'none' : undefined} textAlign={c.align === 'right' ? 'right' : 'left'}>{c.render(row)}</Td>
                      ))}
                      {rowActions && (
                        <Td
                          py={dense ? 1 : undefined}
                          textAlign="right"
                          width={rowActionsFlex ? undefined : rowActionsWidth}
                          whiteSpace="nowrap"
                          onClick={(e) => e.stopPropagation()}
                          pt={rowSubline ? 3 : undefined}
                          pb={rowSubline ? 1 : undefined}
                          borderBottom={rowSubline ? 'none' : undefined}
                          {...pinProps}
                        >
                          {rowActions(row)}
                        </Td>
                      )}
                    </Tr>
                  )}
                  {/* item 1447 — the full-width name line; item 1449 (Chad: "the name on top, one font size
                      smaller, and gray") — small and muted, spanning every data column BELOW the data row
                      (Chad, second look: "the name of the job should be below and not above"); it carries the
                      bottom rule so the pair reads as ONE row. */}
                  {!groupFolded && rowSubline && (
                    <Tr
                      bg={selected.has(id) ? barBg : undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      cursor={onRowClick ? 'pointer' : undefined}
                      sx={onRowClick ? { 'tr:hover + &': { bg: hoverBg } } : undefined}
                      transition="background 0.12s ease"
                      opacity={isRowDimmed?.(row) ? 0.45 : undefined}
                    >
                      <Td
                        colSpan={span - (rowActions ? 1 : 0)}
                        pt={1}
                        pb={dense ? 2 : 3.5}
                        // item 1449 — the name line starts exactly under the IDENTITY cell's text
                        pl={selectable ? '56px' : 4}
                        whiteSpace="normal"
                        fontSize="xs"
                        color={subColor}
                      >
                        {rowSubline(row)}
                      </Td>
                      {rowActions && (
                        <Td
                          pt={1}
                          pb={dense ? 2 : 3.5}
                          onClick={(e) => e.stopPropagation()}
                          {...pinProps}
                        />
                      )}
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      )}

      {hiddenCount > 0 && (
        <Flex justify="center" mt={3}>
          {/* item 1051 — with the history partition, only DONE rows ever hide behind this */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => (isDone ? setDoneLimit((l) => l + doneCap) : setLimit((l) => l + pageSize))}
          >
            {isDone
              ? `Show ${Math.min(doneCap, hiddenCount)} more history`
              : `Load ${Math.min(pageSize, hiddenCount)} more`}
          </Button>
        </Flex>
      )}
    </Box>
  );
}

LedgerTable.defaultProps = {
  defaultSort: undefined,
  bulkActions: undefined,
  rowActions: undefined,
  rowSubline: undefined,
  searchText: undefined,
  searchPlaceholder: 'Search…',
  emptyText: 'Nothing here.',
  caughtUpText: undefined,
  pageSize: 25,
  isDone: undefined,
  doneCap: 30,
  onRowClick: undefined,
  selectable: true,
  groupBy: undefined,
  groupHeader: undefined,
  groupCollapseKey: undefined,
  defaultCollapsed: undefined,
  sectionMuted: undefined,
  rowActionsWidth: undefined,
  rowActionsFlex: false,
  rowActionsHeader: undefined,
  maxSelectable: undefined,
};

export default LedgerTable;
